import { partition } from 'lodash';
import { useWatch } from 'react-hook-form';

import { useFormContext } from '@/components/react-hook-form';
import {
  DISPOSITIVE_PROVISIONS_FORM_V2_NAMESPACE,
  DispositiveProvisionsFormV2Shape,
  PrimaryClientDeathEvent,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.types';
import {
  ContextPrimaryClient,
  useHouseholdDetailsContext,
} from '@/modules/household/contexts/householdDetails.context';
import { useIRSConstants } from '@/modules/irs/useIRSConstants';
import { diagnostics } from '@/utils/diagnostics';

import { useDispositiveProvisionsContext } from '../../contexts/dispositiveProvisions.context';
import { DispositionScheme } from '../../dispositiveProvisions.types';
import { useDispositiveProvisionsTemplateSplitScreenModalContext } from '../../DispositiveProvisionsTemplateSplitScreenModal/DispositiveProvisionsTemplateSplitScreenModal.context';
import { DispositiveProvisionsFormShape } from '../DispositiveProvisionsForm.types';

export function useFirstClientDeathId(): string | null {
  const { control: controlV2 } =
    useFormContext<DispositiveProvisionsFormV2Shape>();
  return useWatch({
    name: `${DISPOSITIVE_PROVISIONS_FORM_V2_NAMESPACE}.selectedFirstPrimaryClientDeathId`,
    control: controlV2,
  });
}

export function useOrderedDyingClients(): [
  ContextPrimaryClient,
  ContextPrimaryClient | undefined,
] {
  const firstClientDeathId = useFirstClientDeathId();
  const { primaryClients } = useDispositiveProvisionsContext();
  const { isTemplateMode } =
    useDispositiveProvisionsTemplateSplitScreenModalContext();

  if (isTemplateMode) {
    return [] as unknown as [
      ContextPrimaryClient,
      ContextPrimaryClient | undefined,
    ];
  }

  // this is kind of odd because each of these arrays with either have zero or one
  // item, but `partition` is kind of the cleanest way to divide these
  const [dyingClients, nonDyingClients] = partition(
    primaryClients,
    (c) => c.id === firstClientDeathId
  );

  if (!dyingClients[0]) {
    throw new Error('Invalid state: no dying client found');
  }

  return [dyingClients[0], nonDyingClients[0]];
}

interface UseActiveClientProfileReturnValue {
  client: ContextPrimaryClient | null;
  isFirstToDie: boolean;
}

/**
 * @description Returns the active client in the dispositive provisions context in the scenario where `isClientProfile: true`.
 * That is, this will return a value when you are setting the dispositions for an individual client and null otherwise.
 */
export function useActiveClientProfile(): UseActiveClientProfileReturnValue {
  const [firstDyingClient] = useOrderedDyingClients();
  const { clientProfileOrEntityOrTestamentaryEntityId, primaryClients } =
    useDispositiveProvisionsContext();

  const client =
    primaryClients.find(
      (client) => client.id === clientProfileOrEntityOrTestamentaryEntityId
    ) ?? null;

  return {
    client,
    isFirstToDie: firstDyingClient.id === client?.id,
  };
}

export function useDispositionScheme(): DispositionScheme {
  const { control } = useFormContext<DispositiveProvisionsFormShape>();
  return useWatch({
    name: 'dispositiveProvisions.dispositionScheme',
    control,
  });
}

export function useHasBeenReviewed(): boolean {
  const { control } = useFormContext<DispositiveProvisionsFormShape>();
  return useWatch({
    name: 'dispositiveProvisions._hasBeenReviewed',
    control,
  });
}

/**
 * @description We don't allow switching between "upon first death" and "upon second death" for testamentary entities or client profiles.
 */
export function useIsImmutableDeathOrder() {
  const { isClientProfile, isTestamentaryEntity } =
    useDispositiveProvisionsContext();
  return isClientProfile || isTestamentaryEntity;
}

export function useDeathOrderAwareClient(
  opts?:
    | {
        deathEvent: PrimaryClientDeathEvent;
      }
    | undefined
): ContextPrimaryClient {
  const dispositionScheme = useDispositionScheme();
  const dyingClients = useOrderedDyingClients();
  const { isTemplateMode } =
    useDispositiveProvisionsTemplateSplitScreenModalContext();

  if (isTemplateMode) {
    return {} as unknown as ContextPrimaryClient;
  }

  if (opts?.deathEvent) {
    // Use the death event to determine the dying primary client.
    switch (opts?.deathEvent) {
      case PrimaryClientDeathEvent.FIRST_DEATH: {
        if (!dyingClients[0]) {
          throw new Error(
            'Invalid state: no first dying client found for deathEvent'
          );
        }
        return dyingClients[0];
      }
      case PrimaryClientDeathEvent.SECOND_DEATH: {
        if (!dyingClients[1]) {
          // Defensive fallback: in single-grantor households, there is no
          // second dying client. This can be reached if the DP context becomes
          // stale (e.g., isTwoClientHousehold flips while the form is open).
          // Return the first client instead of crashing.
          diagnostics.warn(
            'useDeathOrderAwareClient: no second dying client found for SECOND_DEATH deathEvent, falling back to first client'
          );
          return dyingClients[0];
        }
        return dyingClients[1];
      }
      default:
        throw new Error(
          `Requesting client death order in unknown scenario: ${opts?.deathEvent}`
        );
    }
  }

  switch (dispositionScheme) {
    case DispositionScheme.UPON_FIRST_DEATH:
      return dyingClients[0];
    case DispositionScheme.UPON_SECOND_DEATH:
      if (!dyingClients[1]) {
        // Defensive fallback: same rationale as the deathEvent case above.
        diagnostics.warn(
          'useDeathOrderAwareClient: no second dying client found for UPON_SECOND_DEATH, falling back to first client'
        );
        return dyingClients[0];
      }

      return dyingClients[1];
    default:
      throw new Error(
        `Requesting client death order in unknown scenario: ${dispositionScheme}`
      );
  }
}

export function useHasGrantorWithStateTax() {
  const { primaryClients } = useHouseholdDetailsContext();
  const { availableStateEstateTaxes } = useIRSConstants();

  return primaryClients?.some(
    (client) =>
      client.stateCode && availableStateEstateTaxes?.includes(client.stateCode)
  );
}
